// ─── config/taxonomy.js ──────────────────────────────────────────────────────
// The single source of truth for what a service or job can be filed under.
//
// Before this file, three taxonomies disagreed with each other: signup and
// onboarding offered a flat list of "skills", the two creation forms each
// carried their own copy of a category→subcategory map, and Browse Freelancers
// grouped categories into families of its own. Adding a category meant editing
// four files and hoping nothing drifted — and they had already drifted.
//
// Structure is three levels, matching how people actually shop a marketplace:
//
//   family        a short list you can scan without scrolling (8)
//     category    what a listing is filed under (19, including the "Other"
//                 catch-all)
//       subcategory   the specific service (~190, plus whatever a seller
//                     types under "Other")
//
// Families exist because eighteen-plus top-level rows is a wall. They are a
// browsing aid only: listings store a `category` and `subcategory`, never a
// family, so regrouping later is a display change rather than a migration.
//
// The client mirrors this at client/src/constants/taxonomy.js. The two are
// kept honest by testing/tests/taxonomy.test.js, which fails if they drift.

/* ─── Families ─────────────────────────────────────────────────────────────── */

const FAMILIES = [
  { id: 'tech',     label: 'Tech & Data',        categories: ['Programming & Tech', 'AI Services', 'Data'] },
  { id: 'creative', label: 'Design & Creative',  categories: ['Graphics & Design', 'Video & Animation', 'Photography', 'Fine Art'] },
  { id: 'business', label: 'Business & Growth',  categories: ['Business', 'Digital Marketing', 'Finance'] },
  { id: 'writing',  label: 'Writing & Language', categories: ['Writing & Translation'] },
  { id: 'audio',    label: 'Music & Audio',      categories: ['Music & Audio'] },
  { id: 'learning', label: 'Learning & Coaching', categories: ['Academic Support', 'Personal Growth', 'Research'] },
  { id: 'campus',   label: 'Campus & Life',      categories: ['Campus & Events', 'Lifestyle', 'Tangible Goods'] },
  // Kept last on purpose, not alphabetized in — it's the deliberate fallback,
  // not a peer of the other seven families. FAMILIES_ALPHA still sorts it
  // wherever "Other" lands alphabetically for Browse's own family list; only
  // this curated order (CATEGORIES_IN_FAMILY_ORDER, used by the create forms'
  // category dropdown) keeps it trailing.
  { id: 'other',    label: 'Other',              categories: ['Other'] },
];

/* ─── Categories ───────────────────────────────────────────────────────────── */

// `Virtual Tutoring & Coaching` was retired here: it overlapped both of the
// new learning categories, carried no subcategories of its own, and left a
// seller genuinely unsure where academic help belonged. Academic work now
// files under `Academic Support`, life and career coaching under
// `Personal Growth`. CATEGORY_ALIASES below keeps old listings resolving.
const CATEGORIES = {
  'Programming & Tech': [
    'AI and Machine Learning', 'Blockchain Development', 'Cybersecurity',
    'Data Science and Analytics', 'Desktop Applications', 'DevOps & Cloud',
    'Game Development', 'IT Consulting', 'Mobile App Development',
    'QA & Testing', 'Software Development', 'Web Development',
    'WordPress & CMS',
  ],

  'AI Services': [
    'AI Development', 'AI Integration', 'AI Software Engineering',
    'Algorithm Development', 'Business Intelligence Development',
    'Computer Vision Engineering', 'Deep Learning Engineering',
    'Machine Learning Engineering', 'Marketing (AI-Enhanced Tools)',
    'Natural Language Processing Engineering', 'Prompt Engineering',
    'Robotics Engineering', 'UX Design & Development',
  ],

  // New. Data work was previously buried inside AI Services, which hid it from
  // anyone not already searching for AI — and from the many students whose
  // skill is a spreadsheet or a dashboard rather than a model.
  'Data': [
    'Dashboards & Reporting', 'Data Analysis', 'Data Cleaning & Preparation',
    'Data Engineer', 'Data Entry', 'Data Scientist', 'Data Visualization',
    'Database Design', 'Excel & Spreadsheets', 'Market & Survey Data',
    'SQL & Queries', 'Statistical Analysis', 'Web Scraping',
  ],

  'Graphics & Design': [
    'Architecture & Interior Design', 'Book Design', 'Brand Design',
    'Digital Layout', 'Illustration', 'Infographic Design', 'Logo Design',
    'Merchandise Design', 'Motion Graphics', 'Packaging Design',
    'Presentation Design', 'Print Layout Design', 'Social Media Graphics',
    'UI/UX Design', 'Web Design',
  ],

  'Video & Animation': [
    '2D/3D Animating', '3D Model Rigger', 'Animation', 'Character Animation',
    'Concept Art', 'Content Creation', 'Field Production',
    'Motion Graphics Design', 'Post-Production', 'Pre-Production',
    'Storyboard Artist', 'Subtitles & Captions', 'UGC Videos',
    'UI/UX Animation', 'VFX Artist', 'Video Editing',
  ],

  'Photography': [
    'Commercial Photography', 'Event Photography', 'Fashion Photography',
    'Food Photography', 'Photo Editing & Retouching', 'Photojournalism',
    'Portrait Photography', 'Real Estate Photography', 'Sports Photography',
    'Stock Photography', 'Wedding Photography',
    'Wildlife & Landscape Photography',
  ],

  'Fine Art': [
    'Animation & Video', 'Art Curator/Galleries', 'Art Direction',
    'Art Therapy', 'Ceramics & Pottery', 'Commissioned Artwork', 'Drawing',
    'Fine Art Illustration', 'Painting', 'Printmaking', 'Sculpture',
    'Set Design',
  ],

  'Business': [
    'Business Analyst', 'Business Development Consulting',
    'Business Plans & Pitch Decks', 'HR & Recruiting',
    'Legal Consulting', 'Market Research',
    'Marketing Strategy Consulting', 'Operations Consulting',
    'Project Management', 'Sales Strategy Consulting', 'Virtual Assistance',
  ],

  'Digital Marketing': [
    'Affiliate Marketing', 'AI-Driven Marketing', 'Community Management',
    'Content Marketing', 'E-Commerce Consulting', 'Email Marketing',
    'Growth Hacking', 'Influencer Marketing', 'Marketing Analytics',
    'Paid Social Media', 'PPC (Pay-Per-Click) Advertising', 'SEO',
    'Social Media Marketing', 'Video Marketing',
    'Virtual and Augmented Reality Marketing',
  ],

  // New. Accounting and financial work was hiding inside Business, where a
  // student looking to hire a bookkeeper would never think to look.
  'Finance': [
    'Accounting & Bookkeeping', 'Budgeting & Forecasting',
    'Financial Analysis', 'Financial Consulting', 'Financial Modeling',
    'Fundraising & Grants', 'Invoicing & Payroll', 'Personal Finance Coaching',
    'Tax Preparation',
  ],

  'Writing & Translation': [
    'Conference/Community Interpretation', 'Copy Editing/Editing', 'Copywriting',
    'Creative Writing', 'Document Translation', 'Grant Writing',
    'Journalistic Writing', 'Language Consulting', 'Legal/Judicial Translation',
    'Literary Translation', 'Medical Interpretation', 'Resume & Cover Letters',
    'Screenwriting', 'Script Writing', 'Technical Writing',
    'Website Localization',
  ],

  'Music & Audio': [
    'Audio Editing', 'Composer', 'DJ & Live Performance', 'Live Sound Engineering',
    'Mastering Engineering', 'Mix Engineering', 'Music Lessons',
    'Music Production', 'Podcast Production', 'Sound Design',
    'Studio Engineering', 'Voice Over',
  ],

  // New. The most student-native category on the platform, and the one thing
  // a university marketplace can offer that a general one cannot.
  'Academic Support': [
    'Citation & Formatting', 'Essay Coaching', 'Exam Prep',
    'Lab Report Support', 'Literature Review Support', 'Note-Taking',
    'Presentation Coaching', 'Proofreading (Academic)', 'Study Guides',
    'Subject Tutoring', 'Thesis & Dissertation Editing',
    'Writing Center Support',
  ],

  // New.
  'Personal Growth': [
    'Accountability Coaching', 'Career Coaching', 'Fitness Training',
    'Interview Preparation', 'Life Coaching', 'Meditation & Mindfulness',
    'Nutrition Coaching', 'Portfolio Review', 'Productivity Coaching',
    'Public Speaking Coaching', 'Wellness Coaching',
  ],

  // Was empty. Research is a real category on a campus — these are the
  // services students and faculty actually pay each other for.
  'Research': [
    'Academic Research Assistance', 'Data Collection', 'Fact Checking',
    'Interview Transcription', 'Literature Review', 'Market Research',
    'Qualitative Coding', 'Survey Design', 'Systematic Review Support',
  ],

  // New.
  'Campus & Events': [
    'Campus Org Support', 'Catering & Food', 'Decor & Staging',
    'Event Photography', 'Event Planning', 'Event Promotion',
    'Event Videography', 'Live Music & DJ', 'MC & Hosting',
    'Setup & Teardown', 'Ticketing & Registration',
  ],

  // New.
  'Lifestyle': [
    'Arts & Crafts', 'Fashion Styling', 'Gaming & Esports', 'Hair & Makeup',
    'Handmade Gifts', 'Moving & Hauling', 'Pet Care', 'Plant Care',
    'Rideshare & Errands', 'Sustainability & Thrifting',
  ],

  // Was empty.
  'Tangible Goods': [
    'Apparel & Merch', 'Art Prints', 'Baked Goods', 'Ceramics & Pottery',
    'Handmade Jewelry', 'Home & Decor', 'Stationery & Stickers',
    'Textiles & Knitwear', 'Zines & Print',
  ],

  // No curated subcategories on purpose — this is the catch-all for work that
  // fits none of the 18 categories above. The subcategory field is always a
  // free-text combobox (see SubcategoryInput), so an empty list here just
  // means every suggestion under it is one the seller typed themselves.
  'Other': [],
};

/* ─── Normalization ────────────────────────────────────────────────────────── */

// Listings arrived from several sources over the years — the in-app form, a
// Sharetribe import, and hand edits — so the same subcategory is stored as
// `Motion Graphics`, `motion_graphics`, and `motion-graphics`. Slugifying both
// sides collapses casing and separator differences without a hand-written
// entry per variant.
function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Only for values that slugify differently from their canonical label —
// abbreviations, renames, and retired categories. Anything that merely differs
// in case or separator is handled by slugify and does not belong here.
const CATEGORY_ALIASES = {
  'virtual-tutoring-coaching': 'Academic Support',
  'tutoring': 'Academic Support',
  'coaching': 'Personal Growth',
};

const SUBCATEGORY_ALIASES = {
  'logo': 'Logo Design',
  'copy-editing': 'Copy Editing/Editing',
  'content-creator': 'Content Creation',
  'motion-graphics-designer': 'Motion Graphics Design',
  'business-development-consultant': 'Business Development Consulting',
  'production': 'Post-Production',
  'data-analyst': 'Data Analysis',
  'web-dev': 'Web Development',
  'ui-ux': 'UI/UX Design',
};

const CATEGORY_NAMES = Object.keys(CATEGORIES);

// Built once: slug -> canonical label, for categories and for every
// subcategory across all categories.
const CATEGORY_BY_SLUG = new Map(CATEGORY_NAMES.map((c) => [slugify(c), c]));
const SUBCATEGORY_BY_SLUG = new Map();
CATEGORY_NAMES.forEach((cat) => {
  CATEGORIES[cat].forEach((sub) => {
    // First writer wins: a few subcategory labels appear under more than one
    // category (Event Photography, Market Research, Ceramics & Pottery), and
    // for normalization the label is what matters, not which parent it came
    // from — the category field is normalized separately.
    if (!SUBCATEGORY_BY_SLUG.has(slugify(sub))) SUBCATEGORY_BY_SLUG.set(slugify(sub), sub);
  });
});

// Returns the canonical category name, or null when the value can't be
// resolved. Null is deliberate: a migration should report what it could not
// map rather than silently filing listings under a guess.
function normalizeCategory(value) {
  if (!value) return null;
  const slug = slugify(value);
  return CATEGORY_BY_SLUG.get(slug) || CATEGORY_ALIASES[slug] || null;
}

function normalizeSubcategory(value) {
  if (!value) return null;
  const slug = slugify(value);
  return SUBCATEGORY_BY_SLUG.get(slug) || SUBCATEGORY_ALIASES[slug] || null;
}

// Is this subcategory actually valid under this category? Used by the creation
// forms' validation so a client can't post `Web Development` under `Fine Art`.
function isValidPair(category, subcategory) {
  const cat = normalizeCategory(category);
  if (!cat) return false;
  if (!subcategory) return true; // subcategory is optional
  // "Other" has no curated list to check against — every subcategory under
  // it is by definition one the seller typed themselves.
  if (cat === 'Other') return true;
  return CATEGORIES[cat].includes(normalizeSubcategory(subcategory));
}

function familyOf(category) {
  const cat = normalizeCategory(category);
  return FAMILIES.find((f) => f.categories.includes(cat)) || null;
}

module.exports = {
  FAMILIES,
  CATEGORIES,
  CATEGORY_NAMES,
  slugify,
  normalizeCategory,
  normalizeSubcategory,
  isValidPair,
  familyOf,
};
